import all from './all';
import {useChangeStore} from '../../change';
import {useSelectionStore} from '../../selection';
import type {GridEditor, Permissions} from '../../../types';
import {breakItUp, getAppendChar, getUpdatedValueData} from '../../../utils';

import {FormActions, FormValues} from '@netx/core-components/Form';

export default async function loose(
	gridEditor: GridEditor,
	selectionStore: ReturnType<typeof useSelectionStore>,
	changeStore: ReturnType<typeof useChangeStore>,
	initialValues: FormValues[],
	values: FormValues,
	permissions: Permissions,
	actions: FormActions = {},
	setProgress?: (progress: number) => void,
) {
	if (selectionStore.state.all)
		return all(
			gridEditor,
			changeStore,
			initialValues,
			values,
			permissions,
			actions,
			setProgress,
		);

	const {round} = Math;

	const updatingFields = Object.keys(values);

	const appendChar = getAppendChar();

	const data = gridEditor.behavior.dataModel.dataSource.data;
	const columns = gridEditor.behavior.columns;
	const sortIndex = gridEditor.behavior.dataModel.sortIndex;
	const valueColumnObjects = columns.filter((column) =>
		updatingFields.includes(column.name),
	);
	const updatingFieldCount = valueColumnObjects.length;

	const selectedColumns = selectionStore.state.columns;
	const selectedRows = selectionStore.state.rows;
	const selectedRowCount = selectionStore.state.rowCount;

	const selectedColumnObjects = valueColumnObjects.filter((column) =>
		selectedColumns.includes(column.name),
	);
	const selectedColumnCount = selectedColumnObjects.length;
	const columnIndexesUpdating: number[] = selectedColumnObjects.map(
		(column) => column._index,
	);

	let processCount = 3;
	if (!selectedRowCount) processCount--;
	if (!selectedColumnCount) processCount--;
	if (!selectionStore.state.loose) processCount--;

	if (selectedRowCount) {
		// First row updates
		const rowAmount = selectedRowCount <= 100 ? 25 : 100;
		await breakItUp(
			{
				amount: rowAmount,
				size: selectedRowCount,
			},
			(index) => {
				setProgress &&
					setProgress(
						round(((index + 1) / selectedRowCount / processCount) * 100),
					);

				const rowIndex = selectedRows[index];
				const realIndex = sortIndex[rowIndex];
				const rowData = data[realIndex];
				if (!rowData) return;

				const {assetId} = rowData;
				const perms = permissions[assetId];

				for (let x = 0, y = updatingFieldCount; x < y; x++) {
					const column = valueColumnObjects[x];
					if (!column) continue;
					const {name} = column;

					if (perms && perms.includes(name)) continue;

					const valueData = getUpdatedValueData(
						assetId,
						values,
						rowData,
						initialValues,
						column,
						actions,
						appendChar,
					);

					if (!valueData) continue;

					valueData.changed
						? changeStore.add(assetId, name)
						: changeStore.remove(assetId, name);

					rowData[name] = valueData.value;
				}
			},
		);
	}

	if (selectedColumnCount) {
		// Then get columns
		const dataSize = data.length;
		const columnAmount = dataSize <= 100 ? 25 : 100;
		await breakItUp(
			{
				amount: columnAmount,
				size: dataSize,
			},
			(index) => {
				setProgress &&
					setProgress(round(((index + 1) / dataSize / processCount) * 100));

				const realIndex = sortIndex[index];
				const rowData = data[realIndex];
				if (!rowData || selectedRows.includes(index)) return;

				const {assetId} = rowData;
				const perms = permissions[assetId];

				for (let x = 0, y = selectedColumnCount; x < y; x++) {
					const column = selectedColumnObjects[x];
					if (!column) continue;

					const {name} = column;

					if (perms && perms.includes(name)) continue;

					const valueData = getUpdatedValueData(
						assetId,
						values,
						rowData,
						initialValues,
						column,
						actions,
						appendChar,
					);

					if (!valueData) continue;

					valueData.changed
						? changeStore.add(assetId, name)
						: changeStore.remove(assetId, name);

					rowData[name] = valueData.value;
				}
			},
		);
	}

	// Then points
	if (selectionStore.state.loose) {
		const points = await selectionStore.getPoints();
		const pointsSize = points.length;
		const pointAmount = pointsSize <= 100 ? 25 : 100;
		await breakItUp(
			{
				amount: pointAmount,
				size: pointsSize,
				increment: 2,
			},
			(index) => {
				setProgress &&
					setProgress(round(((index + 1) / pointsSize / processCount) * 100));

				const x = points[index];
				const y = points[index + 1];

				const realIndex = sortIndex[y];
				const column = columns[x];
				const rowData = data[realIndex];

				if (
					!rowData ||
					!column ||
					selectedRows.includes(y) ||
					columnIndexesUpdating.includes(x)
				)
					return;

				const {name} = column;
				const {assetId} = rowData;

				const perms = permissions[assetId];
				if (perms && perms.includes(name)) return;

				const valueData = getUpdatedValueData(
					assetId,
					values,
					rowData,
					initialValues,
					column,
					actions,
					appendChar,
				);
				if (!valueData) return;

				valueData.changed
					? changeStore.add(assetId, name)
					: changeStore.remove(assetId, name);

				rowData[name] = valueData.value;
			},
		);
	}

	gridEditor.repaint();
	gridEditor.takeFocus();

	setProgress && setProgress(100);
}
